import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { MuralPage, MuralPost, MuralCommentPage, MuralReactionSummary } from './model/mural.model';

export interface MissionPhotoSubmissionPayload {
  agentMissionId: string;
  missionTitle?: string | null;
  taskId?: string | null;
  taskTitle?: string | null;
  taskDescription?: string | null;
  capturedAt?: string | null;
  sourceType?: 'CAMERA' | 'GALLERY' | 'FILE_UPLOAD';
  requireLocationMatch?: boolean;
  requireSinglePerson?: boolean;
  requireOriginalCapture?: boolean;
  captureLatitude?: number | null;
  captureLongitude?: number | null;
  captureAccuracyMeters?: number | null;
  targetLatitude?: number | null;
  targetLongitude?: number | null;
  targetRadiusMeters?: number | null;
  missionWindowStart?: string | null;
  missionWindowEnd?: string | null;
}

export interface FaceBiometricFaceLocation {
  left?: number | null;
  top?: number | null;
  right?: number | null;
  bottom?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface ProfilePhotoRequest {
  id: string;
  userId?: string;
  userContextId?: string;
  contextId?: string;
  userLogin?: string;
  userName?: string;
  originalFilename: string;
  contentType: string;
  fileSize: number;
  status: string;
  imageUrl?: string | null;
  groupPhotoDetected?: boolean | null;
  detectedFaceCount?: number | null;
  faceLocation?: FaceBiometricFaceLocation | null;
  createdAt: string;
  statusUpdatedAt: string;
}

export interface MissionPhotoValidation {
  id: string;
  validationType: string;
  status: string;
  score?: number | null;
  message?: string | null;
  detailsJson?: string | null;
  createdAt?: string | null;
}

export interface MissionPhotoSubmission {
  id: string;
  userId: string;
  agentMissionId: string;
  taskId?: string | null;
  taskTitle?: string | null;
  originalFilename?: string | null;
  contentType?: string | null;
  fileSize?: number | null;
  imageUrl?: string | null;
  capturedAt?: string | null;
  sourceType?: string | null;
  requireLocationMatch?: boolean | null;
  requireSinglePerson?: boolean | null;
  requireOriginalCapture?: boolean | null;
  captureLatitude?: number | null;
  captureLongitude?: number | null;
  captureAccuracyMeters?: number | null;
  missionWindowStart?: string | null;
  missionWindowEnd?: string | null;
  targetLatitude?: number | null;
  targetLongitude?: number | null;
  targetRadiusMeters?: number | null;
  detectedFaceCount?: number | null;
  groupPhotoDetected?: boolean | null;
  distanceToTargetMeters?: number | null;
  status?: string | null;
  manualReviewReason?: string | null;
  createdAt?: string | null;
  statusUpdatedAt?: string | null;
  validations?: MissionPhotoValidation[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class MissionClientService {
  constructor(private http: HttpClient) {}

  /**
   * Missões do agente no ambiente informado. O escopo é só uma declaração: quem
   * filtra é o servidor, então missão comercial não vaza para dentro do RSM
   * Politics nem que a tela esqueça de esconder.
   */
  public getMissions(scope?: 'COMMERCIAL' | 'POLITICS'): Observable<any[]> {
    const query = scope ? `?scope=${scope}` : '';
    return this.http.get<any[]>(`${environment.agent.url}/agent_mission${query}`);
  }

  public getMission(id: string): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent_mission/${id}`, {});
  }

  public getMissionTasks(id: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.agent.url}/agent_mission/tasks/${id}`);
  }

  public accept(id: string): Observable<any> {
    return this.http.post<any>(`${environment.agent.url}/agent_mission/accept/${id}`, {});
  }

  public refuse(id: string): Observable<any> {
    return this.http.post<any>(`${environment.agent.url}/agent_mission/refuse/${id}`, {});
  }

  public finishTask(id: string, postUrl?: string, proofImageUrl?: string): Observable<any> {
    const body: { postUrl?: string; proofImageUrl?: string } = {};
    if (postUrl && postUrl.trim().length > 0) {
      body.postUrl = postUrl.trim();
    }
    if (proofImageUrl && proofImageUrl.trim().length > 0) {
      body.proofImageUrl = proofImageUrl.trim();
    }
    return this.http.post<any>(`${environment.agent.url}/agent_mission/finish/task/${id}`, body);
  }

  /** Sobe o print de prova (missões por foto) → { proofImageUrl } (chave S3). */
  public uploadMissionProof(file: File): Observable<{ proofImageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<{ proofImageUrl: string }>(
      `${environment.agent.url}/agent_mission/proof/upload`,
      formData
    );
  }

  /**
   * Finaliza a task enviando o PRINT direto (multipart) — o backend valida os bytes
   * na Groq na hora e sobe ao S3 só para auditoria. Evita o round-trip
   * upload→presign→download que era o ponto de falha da validação por foto.
   */
  public verifyTaskWithProof(id: string, file: File, postUrl?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (postUrl && postUrl.trim().length > 0) {
      formData.append('postUrl', postUrl.trim());
    }
    return this.http.post<any>(
      `${environment.agent.url}/agent_mission/finish/task/${id}/proof`,
      formData
    );
  }

  public runTask(id: string): Observable<any> {
    return this.http.post<any>(`${environment.agent.url}/agent_mission/run/task/${id}`, {});
  }

  public finishMisson(id: string): Observable<any> {
    return this.http.post<any>(`${environment.agent.url}/agent_mission/finish/${id}`, {});
  }

  /**
   * Onboarding: declara que o agente NÃO tem conta na rede — conclui a etapa
   * social da trilha sem pontuar (não cria conexão). No-op seguro no backend
   * se o usuário não tiver a missão fixa.
   */
  public skipOnboardingSocial(platform: 'FACEBOOK' | 'INSTAGRAM' | 'THREADS'): Observable<any> {
    return this.http.post<any>(
      `${environment.agent.url}/agent_mission/onboarding/social/${platform}/skip`, {});
  }

  getAgents(): Observable<any> {
    return this.http.get<any>(`${environment.agent.url}/agent/app/user/filter`);
  }

  // ── Mural de Comunicações ──────────────────────────────────────────────
  public submitMissionPhoto(
    payload: MissionPhotoSubmissionPayload,
    file: File
  ): Observable<MissionPhotoSubmission> {
    const formData = new FormData();
    formData.append(
      'payload',
      new Blob([JSON.stringify(payload)], { type: 'application/json' })
    );
    formData.append('file', file, file.name);
    return this.http.post<MissionPhotoSubmission>(
      `${environment.mission.url}/mission-photo-submissions`,
      formData
    );
  }

  public listMissionPhotoSubmissions(
    agentMissionId: string
  ): Observable<MissionPhotoSubmission[]> {
    return this.http.get<MissionPhotoSubmission[]>(
      `${environment.mission.url}/mission-photo-submissions?agentMissionId=${agentMissionId}`
    );
  }

  public reprocessMissionPhotoSubmission(
    submissionId: string
  ): Observable<MissionPhotoSubmission> {
    return this.http.post<MissionPhotoSubmission>(
      `${environment.mission.url}/mission-photo-submissions/${submissionId}/reprocess`,
      {}
    );
  }

  public uploadProfilePhoto(file: File): Observable<ProfilePhotoRequest> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<ProfilePhotoRequest>(
      `${environment.mission.url}/profile-photos`,
      formData
    );
  }

  public getCurrentApprovedProfilePhoto(): Observable<ProfilePhotoRequest | null> {
    return this.http.get<ProfilePhotoRequest | null>(
      `${environment.mission.url}/profile-photos/me/approved`
    );
  }


  /**
   * Feed de comunicações do ambiente informado. Sem o escopo, comunicação
   * comercial aparecia dentro do RSM Politics.
   */
  public getMuralFeed(page = 0, size = 10, scope?: 'COMMERCIAL' | 'POLITICS'): Observable<MuralPage> {
    const params: Record<string, string> = { page: page.toString(), size: size.toString() };
    if (scope) {
      params['scope'] = scope;
    }
    return this.http.get<MuralPage>(`${environment.mission.url}/mural`, { params });
  }

  public deleteMuralPost(postId: number): Observable<void> {
    return this.http.delete<void>(`${environment.mission.url}/mural/${postId}`);
  }

  public toggleMuralReaction(postId: string, reactionType: string): Observable<MuralReactionSummary> {
    // Endpoint real: POST /mural/{id}/reactions com o tipo no body (LIKE|ROGER|MAYDAY).
    return this.http.post<MuralReactionSummary>(
      `${environment.mission.url}/mural/${postId}/reactions`,
      { reactionType }
    );
  }

  public getMuralComments(postId: number, page = 0, size = 20): Observable<MuralCommentPage> {
    return this.http.get<MuralCommentPage>(`${environment.mission.url}/mural/${postId}/comments`, {
      params: { page: page.toString(), size: size.toString() },
    });
  }

  public addMuralComment(postId: number, content: string): Observable<any> {
    return this.http.post<any>(`${environment.mission.url}/mural/${postId}/comments`, { content, isPrivate: true });
  }

  public updateMuralComment(commentId: string, content: string): Observable<any> {
    return this.http.put<any>(`${environment.mission.url}/mural/comments/${commentId}`, { content });
  }

  public deleteMuralComment(commentId: number): Observable<void> {
    return this.http.delete<void>(`${environment.mission.url}/mural/comments/${commentId}`);
  }
}
